import Enums from './enums';
import { isObjectEmpty, isStringEmpty } from './objectUtils';

export const NEW_DASHBOARD_TUTORIAL_STATUSES = {
	UNSEEN: 'UNSEEN',
	COMPLETED: 'COMPLETED',
	SKIPPED_DECLINED: 'SKIPPED_DECLINED',
	SKIPPED_DEFERRED: 'SKIPPED_DEFERRED',
};

/**
 * Whether a value is one of the tutorial statuses.
 *
 * Guards the write path: an unrecognised status would read as neither unseen nor
 * deferred, silently retiring the tutorial with no error anywhere.
 *
 * @param {string} value
 * @returns {boolean}
 */
export const isNewDashboardTutorialStatus = (value) => (
	isStringEmpty(value) ? false : Enums.NewDashboardTutorialStatuses.keys().includes(value)
);

/**
 * Reads a patient's tutorial status off their profile.
 *
 * The redux `profile` is the raw Firestore document, not a constructed `User`, so
 * the model's `= 'UNSEEN'` default never applies to it — every patient predating
 * this field has no value at all. The default is therefore applied here, which is
 * also what makes a backfill unnecessary.
 *
 * @param {object} profile - The redux auth profile.
 * @returns {string} One of NEW_DASHBOARD_TUTORIAL_STATUSES.
 */
export const getNewDashboardTutorialStatus = (profile) => {
	if (isObjectEmpty(profile)) return NEW_DASHBOARD_TUTORIAL_STATUSES.UNSEEN;

	const status = profile?.newDashboardTutorialStatus;

	return isNewDashboardTutorialStatus(status) ? status : NEW_DASHBOARD_TUTORIAL_STATUSES.UNSEEN;
};

/**
 * Whether the patient has never engaged with the tutorial, so it may auto-run.
 *
 * @param {object} profile - The redux auth profile.
 * @returns {boolean}
 */
export const isNewDashboardTutorialUnseen = (profile) => (
	getNewDashboardTutorialStatus(profile) === NEW_DASHBOARD_TUTORIAL_STATUSES.UNSEEN
);

/**
 * Whether the patient skipped the tutorial but asked to see it later, which is the
 * only state that surfaces the avatar-menu entry.
 *
 * @param {object} profile - The redux auth profile.
 * @returns {boolean}
 */
export const isNewDashboardTutorialDeferred = (profile) => (
	getNewDashboardTutorialStatus(profile) === NEW_DASHBOARD_TUTORIAL_STATUSES.SKIPPED_DEFERRED
);
